import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { type Crisis } from '@/lib/types'
import { useHasPermission } from '@/lib/hooks/usePermission'

const updateCrisisSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  location: z.string().min(1, 'Location is required'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
})

type UpdateCrisisFormData = z.infer<typeof updateCrisisSchema>

interface CrisisEditFormProps {
  crisis: Crisis
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CrisisEditForm({ crisis, open, onOpenChange }: CrisisEditFormProps) {
  const { t } = useTranslation()
  const { updateCrisis, isLoading, error } = useCrisisStore()
  const canUpdateCrisis = useHasPermission('crisis.update')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateCrisisFormData>({
    resolver: zodResolver(updateCrisisSchema),
    defaultValues: {
      type: crisis.type,
      location: crisis.location,
      description: crisis.description,
    },
  })

  const onSubmit = async (data: UpdateCrisisFormData) => {
    try {
      await updateCrisis(crisis.id, data)
      onOpenChange(false)
      reset()
    } catch (error) {
      // Error is handled by the store and displayed below
      console.error('Failed to update crisis:', error)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset()
    }
    onOpenChange(newOpen)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-99 p-4">
      <Card className="w-full max-w-md bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('crisis.editCrisis')}</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {!canUpdateCrisis && (
            <div className="mb-4 p-3 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-md dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-800">
              U heeft geen toestemming om deze crisis te bewerken.
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type">{t('crisis.type')}</Label>
              <Input
                id="type"
                {...register('type')}
                placeholder={t('crisis.type')}
                disabled={isLoading || !canUpdateCrisis}
              />
              {errors.type && <p className="text-sm text-red-600">{errors.type.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">{t('crisis.location')}</Label>
              <Input
                id="location"
                {...register('location')}
                placeholder={t('crisis.location')}
                disabled={isLoading || !canUpdateCrisis}
              />
              {errors.location && <p className="text-sm text-red-600">{errors.location.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('crisis.description')}</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder={t('crisis.description')}
                rows={3}
                disabled={isLoading || !canUpdateCrisis}
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              {error && <div className="flex-1 text-sm text-red-600">{error}</div>}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isLoading}
              >
                {t('algemeen.annuleren', 'Cancel')}
              </Button>
              <Button type="submit" variant="gradient" disabled={isLoading || !canUpdateCrisis}>
                {isLoading
                  ? t('algemeen.laden', 'Updating...')
                  : t('crisis.editCrisis', 'Update Crisis')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
